import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box
} from '@mui/material';
import { Warning } from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';

interface ConfirmationDialogProps {
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    onCancel: () => void;
    confirmText?: string;
    cancelText?: string;
    severity?: 'warning' | 'error' | 'info';
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
    open,
    title,
    message,
    onConfirm,
    onCancel,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    severity = 'warning'
}) => {
    const { theme } = useTheme();

    const getSeverityColor = () => {
        switch (severity) {
            case 'error':
                return theme.palette.error.main;
            case 'warning':
                return theme.palette.warning.main;
            case 'info':
                return theme.palette.info.main;
            default:
                return theme.palette.warning.main;
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onCancel}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: theme.palette.background.paper,
                    borderRadius: 2
                }
            }}
        >
            <DialogTitle sx={{ 
                color: theme.palette.text.primary,
                borderBottom: `1px solid ${theme.palette.divider}`,
                pb: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Warning sx={{ color: getSeverityColor() }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {title}
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ pt: 3 }}>
                <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                    {message}
                </Typography>
            </DialogContent>

            <DialogActions sx={{ 
                p: 3, 
                borderTop: `1px solid ${theme.palette.divider}`,
                gap: 1
            }}>
                <Button
                    onClick={onCancel}
                    variant="outlined"
                    sx={{ 
                        color: theme.palette.text.secondary,
                        borderColor: theme.palette.divider
                    }}
                >
                    {cancelText}
                </Button>
                <Button
                    onClick={onConfirm}
                    variant="contained"
                    sx={{
                        bgcolor: getSeverityColor(),
                        '&:hover': {
                            bgcolor: getSeverityColor(),
                            opacity: 0.9
                        }
                    }}
                >
                    {confirmText}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmationDialog; 